import './OnboardingSummaryContent.scss'

import type { Onboarding, OnboardingUser } from '@op/services'
import cls from 'classnames'
import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { Glyph } from '../../../../../widgets'
import { useOnboardingSummary } from '../../../hooks/useOnboardingSummary'
import type { ColRuleItemProps } from '../../../types'

type OnboardingSummaryContentProps = {
	readonly onboarding: Onboarding
	readonly selectedIdx: number
	readonly users: OnboardingUser[]
}

export const OnboardingSummaryContent = ({
	onboarding,
	selectedIdx,
	users,
}: OnboardingSummaryContentProps) => {
	const { allowance, appRules, locationAlerts, schedules, vew, websiteRules } =
		useOnboardingSummary(onboarding, users[selectedIdx])
	const hasSchedules = schedules.length > 0
	const hasVew = vew.length > 0
	const hasLocationAlerts = locationAlerts.length > 0

	return (
		<div className="onboarding-summary">
			<OnboardingRuleSection name="Screen Time Rules">
				<RowLayout>
					<OnboardingSectionBox className="h-full flex flex-col md:order-1">
						<OnboardingSectionTitle>Daily Limits</OnboardingSectionTitle>
						<div className="h-full flex flex-col justify-center gap-2 md:gap-4 mt-4 md:mt-2">
							{allowance.map(({ id, status, title }) => (
								<div key={id} className="flex justify-between">
									<h6 className="font-bold text-sm">{title}</h6>
									<p className="font-bold text-sm">{status}</p>
								</div>
							))}
						</div>
					</OnboardingSectionBox>

					{hasSchedules && (
						<OnboardingSectionBox className="h-full flex flex-col gap-2">
							<OnboardingSectionTitle>Schedules</OnboardingSectionTitle>

							<div className="flex items-center justify-evenly gap-2">
								{schedules.map((s, idx) => (
									<Fragment key={s.label}>
										<ImgItem img={s.icon} label={s.label} />
										{idx < schedules.length - 1 && <RuleSeparator />}
									</Fragment>
								))}
							</div>
						</OnboardingSectionBox>
					)}
				</RowLayout>
			</OnboardingRuleSection>

			<OnboardingRuleSection name="App Rules">
				<div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-3">
					{appRules.map((rule) => (
						<ColRuleItem key={rule.id} {...rule} />
					))}
				</div>
			</OnboardingRuleSection>

			<OnboardingRuleSection name="Website Rules">
				<RowLayout>
					{websiteRules.map((rule) => (
						<ColRuleItem key={rule.id} className="sm:p-5 sm:pr-3" {...rule} />
					))}
				</RowLayout>
			</OnboardingRuleSection>

			<OnboardingRuleSection name="Location Sharing">
				<RowLayout>
					<OnboardingSectionBoxSmall
						className="h-fit md:order-1"
						status="On"
						subtitle="Includes 2 weeks history"
						title="Track Location"
					/>

					{hasLocationAlerts && (
						<OnboardingSectionBox>
							<OnboardingSectionTitle>Location Alerts</OnboardingSectionTitle>

							<div className="flex items-center justify-evenly mt-4">
								{locationAlerts.map((location, idx) => (
									<Fragment key={location.label}>
										<ImgItem img={location.icon} label={location.label} />
										{idx < locationAlerts.length - 1 && <RuleSeparator />}
									</Fragment>
								))}
							</div>
						</OnboardingSectionBox>
					)}
				</RowLayout>
			</OnboardingRuleSection>

			{hasVew && (
				<OnboardingRuleSection name="Content Monitor">
					<RowLayout>
						{vew.map((v) => (
							<OnboardingSectionBoxSmall key={v.id} {...v} />
						))}
					</RowLayout>
				</OnboardingRuleSection>
			)}
		</div>
	)
}

const ColRuleItem = ({ className, colorClassName, icon, subtitle, title }: ColRuleItemProps) => (
	<div
		className={cls(
			'bg-teal-hl p-2 pl-4 w-full rounded-2xl flex items-center justify-between min-h-16 sm:min-h-18',
			className,
		)}
	>
		<div className="flex flex-col">
			<h6 className="text-sm sm:text-base font-semibold leading-tight mb-1 sm:mb-0">{title}</h6>
			{subtitle && <p className="text-xs sm:text-sm leading-tight">{subtitle}</p>}
		</div>

		<Glyph name={icon} className={cls('mr-1 sm:mr-2 ml-2 sm:ml-3', colorClassName)} />
	</div>
)

type ImgItemProps = {
	readonly img: string
	readonly label: string
}

const ImgItem = ({ img, label }: ImgItemProps) => (
	<div className="flex flex-auto flex-col items-center gap-2">
		<img className="max-w-16 sm:max-w-18 w-full" src={img} alt={label} width={24} height={24} />
		<p className="text-sm font-bold pb-2">{label}</p>
	</div>
)

type OnboardingRuleSectionProps = {
	readonly name: string
	readonly children: ReactNode
}

const OnboardingRuleSection = ({ name, children }: OnboardingRuleSectionProps) => (
	<div className="w-full flex flex-col gap-4">
		<h3 className="text-xl text-charcoal font-bold">{name}</h3>
		{children}
	</div>
)

type OnboardingSectionBoxProps = {
	readonly className?: string
	readonly children: ReactNode
}

/**
 * Component to display a styled box within a section of the onboarding summary screen.
 */
const OnboardingSectionBox = ({ className, children }: OnboardingSectionBoxProps) => (
	<div className={cls('flex flex-col bg-teal-hl p-4 sm:p-5 rounded-2xl', className)}>
		{children}
	</div>
)

type OnboardingSectionBoxSmallProps = {
	readonly className?: string
	readonly status: string
	readonly subtitle?: string
	readonly title: string
}

/**
 * Component to display a small styled box within a section of the onboarding summary screen.
 *
 * - Provides stylings for status, subtitle, and title.
 */
const OnboardingSectionBoxSmall = ({
	className,
	status,
	subtitle,
	title,
}: OnboardingSectionBoxSmallProps) => (
	<OnboardingSectionBox className={className}>
		<div className="flex items-center justify-between h-full gap-2">
			<div className="flex-auto self-start">
				<OnboardingSectionTitle>{title}</OnboardingSectionTitle>
				{subtitle && (
					<p className="capitalize text-xs sm:text-sm leading-tight sm:leading-tight">{subtitle}</p>
				)}
			</div>

			<p className="font-bold text-teal text-sm sm:text-base">{status}</p>
		</div>
	</OnboardingSectionBox>
)

type OnboardingSectionTitleProps = {
	readonly children: string
}

const OnboardingSectionTitle = ({ children }: OnboardingSectionTitleProps) => (
	<h5 className="capitalize text-sm sm:text-base font-bold leading-tight mb-1">{children}</h5>
)

type RowLayoutProps = {
	readonly children: ReactNode
	readonly className?: string
}

const RowLayout = ({ children, className }: RowLayoutProps) => (
	<div className={cls('w-full grid md:grid-cols-2 grid-cols-1 items-stretch gap-4 ', className)}>
		{children}
	</div>
)

const RuleSeparator = () => <hr className="h-15/16 w-px bg-gray-300" aria-hidden="true" />
